import React, { useState, useContext, useEffect } from 'react';
import {
  Settings, Smartphone, Network, ChevronLeft,
  ToggleLeft, User, Server, Apple, Bluetooth
} from 'lucide-react';
import { AppContext } from '../../context/AppContext';
import { SettingsSection, SettingsRow } from './SettingsUI';
import LoadingSpinner from '../common/LoadingSpinner';

import GeneralSettings from './GeneralSettings';
import ZigbeeSettings from './ZigbeeSettings';
import FRPSettings from './FRPSettings';
import UserSettings from './UserSettings';
import FeatureSettings from './FeatureSettings';
import SystemSettings from './SystemSettings';
import HomeKitSettings from './HomeKitSettings';
import BluetoothSettings from './BluetoothSettings';

const settingsGroups = [
  {
    title: 'System',
    items: [
      { key: 'general', icon: <Settings className="w-4 h-4" />, label: 'General', component: GeneralSettings },
      { key: 'system', icon: <Server className="w-4 h-4" />, label: 'System Services', component: SystemSettings },
      { key: 'features', icon: <ToggleLeft className="w-4 h-4" />, label: 'Features', component: FeatureSettings },
    ]
  },
  {
    title: 'Integrations',
    items: [
      { key: 'homekit', icon: <Apple className="w-4 h-4" />, label: 'Apple HomeKit', component: HomeKitSettings },
      { key: 'zigbee', icon: <Smartphone className="w-4 h-4" />, label: 'Zigbee', component: ZigbeeSettings },
      { key: 'bluetooth', icon: <Bluetooth className="w-4 h-4" />, label: 'Bluetooth', component: BluetoothSettings },
      { key: 'frp', icon: <Network className="w-4 h-4" />, label: 'Remote Access', component: FRPSettings },
    ]
  },
  {
    title: 'Account',
    items: [
      { key: 'user', icon: <User className="w-4 h-4" />, label: 'User Account', component: UserSettings },
    ]
  }
];

const allItems = settingsGroups.flatMap(g => g.items);

const SettingsPanel = () => {
  const { config, fetchConfig, isLoading } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState(null);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        await fetchConfig();
        setIsConfigLoaded(true);
      } catch (error) {
        console.error('Failed to load configuration:', error);
      }
    };

    if (!isConfigLoaded) {
      loadConfig();
    }
  }, [fetchConfig, isConfigLoaded]);

  if (isLoading || !isConfigLoaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="large" text="Loading settings..." />
      </div>
    );
  }

  const activeItem = allItems.find(item => item.key === activeTab);

  if (activeTab && activeItem) {
    return (
      <div className="min-h-screen bg-background">
        <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/40">
          <div className="flex items-center gap-2 px-4 h-12">
            <button
              onClick={() => setActiveTab(null)}
              className="flex items-center gap-1 text-sm text-primary font-medium"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Settings</span>
            </button>
          </div>
        </div>
        <div className="px-4 py-4">
          {React.createElement(activeItem.component, { config })}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0 py-0">
      <div className="px-4 pt-6 pb-4">
        <h1 className="text-2xl font-semibold">Settings</h1>
      </div>

      <div className="px-4 space-y-6 pb-8">
        {settingsGroups.map(group => (
          <SettingsSection key={group.title} title={group.title}>
            {group.items.map(item => (
              <SettingsRow
                key={item.key}
                label={item.label}
                icon={item.icon}
                chevron
                onClick={() => setActiveTab(item.key)}
              />
            ))}
          </SettingsSection>
        ))}
      </div>
    </div>
  );
};

export default SettingsPanel;
